import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClassroomFilterDto } from 'src/app/data/models/dto/classroom-filter.dto';
import { ClassRoomsService } from 'src/app/data/services/classroom.service';
import { ProfileService } from 'src/app/data/services/profile.service';
import {
  ExcludeProfessorAndEmployees,
  formatSelectedProfilesText,
} from 'src/app/data/utils/profile.utils';
import { UserUtil } from '../../../../data/utils/user.utils';
import { FlatNode } from '../../../../ui/components/select-hierarquical-list/model/flat.node.interface';
import { SimpleNode } from '../../../../ui/components/select-hierarquical-list/model/simple.node.interface';
import { ImageItem } from '../../../../ui/components/select-simple-list/model/item-image.interface';
import { Channel } from '../../models/channel.interface';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ContractedFeatureUtil } from 'src/app/data/utils/contracted-feature.utils';

@Component({
  selector: 'app-channel-form',
  templateUrl: './channel-form.component.html',
  styleUrls: ['./channel-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChannelFormComponent implements OnInit, AfterViewInit {
  @Output() onUpdateChannel = new EventEmitter<Channel>();
  @Output() onCreateChannel = new EventEmitter<Channel>();
  @Output() onChangeYear = new EventEmitter<string>();

  get isUpdate() {
    return this.channel?.id ? true : false;
  }

  private get schools() {
    return this.userUtil.getSchools();
  }

  channel?: Channel = this.data.channel;

  formGroup: FormGroup = this.fb.group({
    name: ['', [Validators.required]],
    employees: ['', [Validators.required]],
    classrooms: ['', [Validators.required]],
    profiles: ['', [Validators.required]],
    ticketFunctionality: [false],
    description: [''],
  });

  // segments
  segmentTreeData: SimpleNode[] = [] ;
  idsClassRoomsAdded: Set<string> = new Set();
  idsClassRoomsRemoved: Set<string> = new Set();
  classRooms: string[] = [];

  hasClassrooms: boolean = true;
  alertMessage = 'Este canal não poderá ser criado pois ainda não existem turmas cadastradas para o ano letivo selecionado.';

  // employees
  employees: ImageItem[] = [];
  employeePlaceholder = 'Atendentes';
  selectAllEmployees = 'Selecione todos os atendentes';
  idsEmployees: string[] = [];

  //year
  startTxtYear = new Date().getFullYear().toString();

  // profiles
  profilePlaceholder = 'Perfis de acesso';
  selectAll = 'Todos os perfis';
  idsProfiles: string[] = [];
  excludeProfiles = ExcludeProfessorAndEmployees;

  //tickets
  matToggle?: MatSlideToggle;
  ticketFunctionalityActive: boolean = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly classroomsService: ClassRoomsService,
    private readonly userUtil: UserUtil,
    private profileService: ProfileService,
    private contractedFeatureUtil: ContractedFeatureUtil,
    @Inject(MAT_DIALOG_DATA)
    private readonly data: {
      channel?: Channel;
      employees: ImageItem[];
      profiles: ImageItem[];
    }
  ) {}

  ngAfterViewInit(): void {
    this.formGroup.markAsPristine();
  }

  async ngOnInit() {
    this.updateEmployeesList();
    const filter: ClassroomFilterDto = {
      year: this.startTxtYear,
      idSchool: this.schools[0].id,
    };
    await this.loadClassroomsTree(filter);
    if (this.isUpdate) {
      this.loadChannelData(this.channel!);
      this.loadProfiles(this.channel!);
    }
  }

  private async loadClassroomsTree(
    filter: ClassroomFilterDto
  ): Promise<SimpleNode[]> {
    try {
      const tree = await this.classroomsService.getTree(filter).toPromise();
      const treeNode: SimpleNode[] = tree.map((seg) => ({
        id: seg.id,
        name: seg.name,
        children: seg.series.map((ser) => ({
          name: ser.name,
          children: ser.classRooms.map((cls) => ({
            id: cls.id,
            name: cls.name,
          })),
        })),
      }));
      this.segmentTreeData = treeNode;
      this.updateClassroomsControlState();
      return treeNode;
    } catch (error: any) {
      this.hasClassrooms = false;

      if (error.status === 404) {
        this.segmentTreeData = [];
        this.alertMessage;
        this.updateClassroomsControlState();
        return [];
      } else {
        console.error(error);
        return [];
      }
    }
  }

private updateClassroomsControlState(): void {
  const control = this.formGroup.get('classrooms');

  if (!this.hasClassrooms) {
    control?.disable({ emitEvent: false });
  } else {
    control?.enable({ emitEvent: false });
  }
}

  hasTicketsFeatures(){
    return this.contractedFeatureUtil.hasTicketsFeature();
  }

  loadProfiles(channel: Channel) {
    this.idsProfiles = channel.idsProfiles;
  }

  //#region lists data
  loadChannelData(channel: Channel) {
    this.loadFormChannelName(channel.name);
    this.loadFormChannelDescription(channel.description);
    this.loadFormClassroomsName(channel.idsClassRooms);
    this.loadFormEmployeesName(channel.idsEmployees);
    this.loadProfilesName(channel.idsProfiles);
    this.loadTicketFunctionalityCheck(channel.ticketFunctionalityActive);
  }

  loadFormChannelName(name: string) {
    this.formGroup.controls['name'].setValue(name);
  }

  loadFormChannelDescription(description: string) {
    this.formGroup.controls['description'].setValue(description);
  }

  loadFormClassroomsName(idsClassRooms: string[]) {
    let classroomsNames = '';
    this.segmentTreeData.forEach((segment) => {
      segment.children?.forEach((serie) => {
        serie.children?.forEach((classroom) => {
          if (idsClassRooms.includes(classroom.id!)) {
            if (classroomsNames === '') classroomsNames += classroom.name;
            else classroomsNames += ',' + classroom.name;
          }
        });
      });
    });
    this.formGroup.controls['classrooms'].setValue(classroomsNames);
  }

  loadFormEmployeesName(idsEmployees: string[]) {
    this.idsEmployees = idsEmployees;

    let employeesName = '';
    this.employees.forEach(async (employee) => {
      if (idsEmployees.includes(employee.id!)) {
        if (employeesName == '') {
          employeesName = employee.name.trim();
        } else {
          employeesName = employeesName + ', ' + employee.name.trim();
        }
      }
    });

    this.formGroup.controls['employees'].setValue(employeesName);
  }

  async loadProfilesName(idsProfiles: string[]) {
    this.formGroup.controls['profiles'].setValue(
      await this.formatProfilesText(idsProfiles)
    );
  }

  loadTicketFunctionalityCheck(ticketFunctionality?: boolean){
    this.ticketFunctionalityActive = ticketFunctionality ?? false;
    this.formGroup.controls['ticketFunctionality'].setValue(this.ticketFunctionalityActive);
  }
  //#endregion

  saveChannel() {
    if (this.channel) {
      this.channel.idsClassRooms = this.channel.idsClassRooms.filter(
        (id) => !this.idsClassRoomsRemoved.has(id)
      );
      this.channel.idsClassRooms.push(...this.idsClassRoomsAdded);
    } else {
      this.idsClassRoomsRemoved.forEach((id) => {
        this.idsClassRoomsAdded.delete(id);
      });
    }
    const channel = {
      id: this.channel?.id,
      name: this.formGroup.value.name,
      active: true,
      idsClassRooms: this.channel
        ? Array.from(new Set(this.channel?.idsClassRooms))
        : Array.from(this.idsClassRoomsAdded),
      idsEmployees: this.idsEmployees,
      idSchool: this.schools[0].id,
      idsProfiles: this.idsProfiles,
      description: this.formGroup.value.description,
      ticketFunctionalityActive: this.formGroup.value.ticketFunctionality ? true : false,
    } as Channel;

    console.log(channel);

    if (!this.isUpdate) {
      this.onCreateChannel.emit(channel);
    } else {
      this.onUpdateChannel.emit(channel);
    }
  }

  openList(evt: any, trigger: MatAutocompleteTrigger): void {
    evt.stopPropagation();
    if (trigger.panelOpen) {
      trigger.closePanel();
    } else {
      trigger.openPanel();
    }
  }

  //#region handle lists select
  handleClassroomSelect(evt: FlatNode[]) {
    this.classRooms = evt.map((evt) => evt.name);
    evt.forEach((evt) => {
      this.idsClassRoomsAdded.add(evt.id);
    });

    this.formGroup.controls['classrooms'].setValue(this.classRooms);
    this.formGroup.markAsDirty();
  }

  handleDeselected(evt: FlatNode[]) {
    evt.forEach((evt) => {
      this.idsClassRoomsRemoved.add(evt.id);
      this.idsClassRoomsAdded.delete(evt.id);
    });
  }

  handleEmployeesSelect(evt: ImageItem[]): void {
    const names = evt.filter((item) => item.isChecked).map((item) => item.name);

    this.formGroup.controls['employees'].setValue(names);

    this.idsEmployees = evt
      .filter((item) => item.isChecked)
      .map((item) => item.id) as string[];
    this.formGroup.markAsDirty();
  }

  async handleYearSelected(year: string) {
    this.segmentTreeData = [];

    this.classRooms = [];
    const filter: ClassroomFilterDto = {
      year: year,
      idSchool: this.schools[0].id,
    };
    await this.loadClassroomsTree(filter);
    if (this.channel?.idsClassRooms) {
      this.loadFormClassroomsName(this.channel.idsClassRooms);
    }
  }

  private updateEmployeesList(): void {
    this.data.employees.forEach((employe) => {
      const item = {
        id: employe.id,
        imageUrl: employe.imageUrl,
        name: employe.name,
        isChecked: this.isEmployeChecked(employe.id!),
      };
      this.employees.push(item);
    });
  }

  private isEmployeChecked(userId: string): boolean {
    if (this.data.channel) {
      return this.data.channel.idsEmployees.includes(userId);
    } else {
      return false;
    }
  }

  private async formatProfilesText(
    selectedIdsProfiles: string[]
  ): Promise<string> {
    const result = await this.profileService.getAll().toPromise();
    const profiles = result.map(
      (profile) =>
        <ImageItem>{
          id: profile.id,
          imageUrl: '',
          name: profile.name,
          isChecked: selectedIdsProfiles.includes(profile.id!),
        }
    );
    const profileNames = formatSelectedProfilesText(profiles);
    return profileNames;
  }

  handleProfilesSelect(evt: ImageItem[]): void {
    const names = formatSelectedProfilesText(evt);

    this.formGroup.controls['profiles'].setValue(names);

    this.idsProfiles = evt
      .filter((item) => item.isChecked)
      .map((item) => item.id) as string[];
    this.formGroup.markAsDirty();
  }

  onClick(event: any): void {
    event.stopPropagation();
  }

  onChange(event: MatSlideToggleChange) {
    this.matToggle = event.source;
    this.ticketFunctionalityActive = event.checked ? true : false;
    this.matToggle.checked = this.ticketFunctionalityActive;
    this.formGroup.controls['ticketFunctionality'].setValue(this.ticketFunctionalityActive);
  }

  isChecked(){
    if(this.isUpdate){
      return this.ticketFunctionalityActive;
    }
    return false;
  }
  //#endregion
}
